import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Doc, Id } from "../../convex/_generated/dataModel";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/cn";
import { clientLogTimes } from "@/lib/dayHelpers";
import { defaultMealType } from "./meal-review/review-utils";
import {
  EMPTY_MEAL_FORM,
  buildQuickAddLogArgs,
  parseNonnegativeMetric,
  validateMealForm,
} from "./meal-form-utils";
import type { MealFormFields, MealFormFlow } from "./meal-form-utils";

type MealFormProps = {
  readonly date: number;
  readonly onClose: () => void;
  // "legacy" (default): Today sheet — calories required, entries.createLoggedFood.
  // "quickAdd": quick-add flow — calories optional (blank = unknown), single-item
  // api.meals.logMealBatch with source "manual" / dataSource "user-entered".
  readonly flow?: MealFormFlow;
};

type MealFormState = MealFormFields;

const EMPTY_FORM: MealFormState = EMPTY_MEAL_FORM;

export function MealForm({ date, onClose, flow = "legacy" }: MealFormProps) {
  const [form, setForm] = useState<MealFormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createLoggedFood = useMutation(api.entries.createLoggedFood).withOptimisticUpdate(
    (localStore, args) => {
      const current = localStore.getQuery(api.dashboard.getByDate, { date: args.date, clientDayStart: args.clientDayStart });
      if (current === undefined) return;

      const now = Date.now();
      const temporaryEntry: Doc<"foodEntries"> = {
        _id: `temp_${now}_${Math.random().toString(36).slice(2)}` as Id<"foodEntries">,
        _creationTime: now,
        userId: "optimistic" as Id<"users">,
         date: args.clientDayStart ?? args.date,
        name: args.name,
        estimatedKcal: args.estimatedKcal,
        finalKcal: args.estimatedKcal,
        proteinG: args.proteinG,
        carbsG: args.carbsG,
        fatG: args.fatG,
        fiberG: args.fiberG,
        source: "text",
        status: "logged",
        notes: args.notes,
        loggedAt: now,
      };

      const addKnownMetric = (currentValue: number | null, addedValue: number | undefined): number | null => {
        if (addedValue === undefined) return currentValue;
        return (currentValue ?? 0) + addedValue;
      };

       localStore.setQuery(api.dashboard.getByDate, { date: args.date, clientDayStart: args.clientDayStart }, {
        ...current,
        consumedKcal: current.consumedKcal + args.estimatedKcal,
        netKcal: current.netKcal + args.estimatedKcal,
        remainingKcal: current.remainingKcal - args.estimatedKcal,
        consumedProteinG: addKnownMetric(current.consumedProteinG, args.proteinG),
        consumedCarbsG: addKnownMetric(current.consumedCarbsG, args.carbsG),
        consumedFatG: addKnownMetric(current.consumedFatG, args.fatG),
        consumedFiberG: addKnownMetric(current.consumedFiberG, args.fiberG),
        foodEntries: [...current.foodEntries, temporaryEntry],
      });
    },
  );

  // Quick-add flow (todo 18): single-item logMealBatch. Unknown calories only
  // bump unknownKcalCount and leave every calorie sum untouched — never 0.
  const logMealBatch = useMutation(api.meals.logMealBatch).withOptimisticUpdate(
    (localStore, args) => {
      const current = localStore.getQuery(api.dashboard.getByDate, { date: args.date, clientDayStart: args.clientDayStart });
      if (current === undefined) return;
      const item = args.items[0];
      if (item === undefined) return;

      const now = Date.now();
      const kcal = item.finalKcal ?? item.estimatedKcal;
      const temporaryEntry: Doc<"foodEntries"> = {
        _id: `temp_${now}_${Math.random().toString(36).slice(2)}` as Id<"foodEntries">,
        _creationTime: now,
        userId: "optimistic" as Id<"users">,
        date: args.clientDayStart ?? args.date,
        name: item.name,
        ...(kcal === undefined ? {} : { estimatedKcal: kcal, finalKcal: kcal }),
        ...(item.proteinG === undefined ? {} : { proteinG: item.proteinG }),
        ...(item.carbsG === undefined ? {} : { carbsG: item.carbsG }),
        ...(item.fatG === undefined ? {} : { fatG: item.fatG }),
        ...(item.fiberG === undefined ? {} : { fiberG: item.fiberG }),
        mealType: args.mealType,
        ...(item.kcalUnknown === true ? { kcalUnknown: true } : {}),
        ...(item.dataSource === undefined ? {} : { dataSource: item.dataSource }),
        source: "manual",
        status: "logged",
        ...(item.notes === undefined ? {} : { notes: item.notes }),
        loggedAt: args.clientLoggedAt ?? now,
      };

      const addKnownMetric = (currentValue: number | null, addedValue: number | undefined): number | null => {
        if (addedValue === undefined) return currentValue;
        return (currentValue ?? 0) + addedValue;
      };

      localStore.setQuery(api.dashboard.getByDate, { date: args.date, clientDayStart: args.clientDayStart }, {
        ...current,
        consumedKcal: kcal === undefined ? current.consumedKcal : current.consumedKcal + kcal,
        netKcal: kcal === undefined ? current.netKcal : current.netKcal + kcal,
        remainingKcal: kcal === undefined ? current.remainingKcal : current.remainingKcal - kcal,
        consumedProteinG: addKnownMetric(current.consumedProteinG, item.proteinG),
        consumedCarbsG: addKnownMetric(current.consumedCarbsG, item.carbsG),
        consumedFatG: addKnownMetric(current.consumedFatG, item.fatG),
        consumedFiberG: addKnownMetric(current.consumedFiberG, item.fiberG),
        unknownKcalCount: current.unknownKcalCount + (item.kcalUnknown === true ? 1 : 0),
        foodEntries: [...current.foodEntries, temporaryEntry],
      });
    },
  );

  const update = (field: keyof MealFormState, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const validationError = validateMealForm(form, flow);
    if (validationError !== null) {
      setError(validationError);
      return;
    }

    const name = form.name.trim();
    const proteinG = parseNonnegativeMetric(form.protein);
    const carbsG = parseNonnegativeMetric(form.carbs);
    const fatG = parseNonnegativeMetric(form.fat);
    const fiberG = parseNonnegativeMetric(form.fiber);
    const notes = form.notes.trim();

    setIsSaving(true);
    if (flow === "quickAdd") {
      const mealType = defaultMealType(new Date().getHours());
      const logArgs = buildQuickAddLogArgs({ form, date, ...clientLogTimes(date), mealType });
      const kcalUnknown = logArgs.items[0]?.kcalUnknown === true;
      console.log("[Frontend][MealForm] Quick add logging food", {
        date,
        name,
        mealType,
        kcalUnknown,
        hasMacros: proteinG !== undefined || carbsG !== undefined || fatG !== undefined || fiberG !== undefined,
        hasNotes: notes.length > 0,
      });
      try {
        await logMealBatch(logArgs);
        console.log("[Frontend][MealForm] Quick add food logged", { date, name, kcalUnknown });
        setForm(EMPTY_FORM);
        setError(null);
        onClose();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        console.error("[Frontend][MealForm] Quick add logging failed", { date, name, error: message });
        setError("Could not log that food. Please try again.");
      } finally {
        setIsSaving(false);
      }
      return;
    }

    const estimatedKcal = parseNonnegativeMetric(form.kcal);
    if (estimatedKcal === undefined) {
      setIsSaving(false);
      setError("Calories must be non-negative.");
      return;
    }
    console.log("[Frontend][MealForm] Logging meal", {
      date,
      name,
      estimatedKcal,
      hasMacros: proteinG !== undefined || carbsG !== undefined || fatG !== undefined || fiberG !== undefined,
      hasNotes: notes.length > 0,
    });
    try {
      await createLoggedFood({
        date,
        ...clientLogTimes(date),
        name,
        estimatedKcal,
        ...(proteinG !== undefined ? { proteinG } : {}),
        ...(carbsG !== undefined ? { carbsG } : {}),
        ...(fatG !== undefined ? { fatG } : {}),
        ...(fiberG !== undefined ? { fiberG } : {}),
        ...(notes.length > 0 ? { notes } : {}),
      });
      console.log("[Frontend][MealForm] Meal logged", { date, name, estimatedKcal });
      setForm(EMPTY_FORM);
      setError(null);
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      console.error("[Frontend][MealForm] Meal logging failed", { date, name, error: message });
      setError("Could not log that meal. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const isQuickAdd = flow === "quickAdd";

  return (
    <div data-testid="meal-form" data-flow={flow} className="text-left">
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <Input data-testid="meal-name-input" label="Name" value={form.name} onChange={(event) => update("name", event.target.value)} placeholder="e.g. Oatmeal" aria-required="true" />
        <div className="flex flex-col gap-1.5">
          <Input
            data-testid="meal-kcal-input"
            label="Calories (kcal)"
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            value={form.kcal}
            onChange={(event) => update("kcal", event.target.value)}
            placeholder={isQuickAdd ? "Optional" : "350"}
            {...(isQuickAdd ? {} : { "aria-required": "true" })}
          />
          {isQuickAdd && (
            <p data-testid="meal-kcal-hint" className="text-xs text-text-muted">
              Not sure? Leave it blank — the calories stay unknown, not zero.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input data-testid="meal-protein-input" label="Protein (g)" type="number" min="0" step="0.1" inputMode="decimal" value={form.protein} onChange={(event) => update("protein", event.target.value)} placeholder="Optional" />
          <Input data-testid="meal-carbs-input" label="Carbs (g)" type="number" min="0" step="0.1" inputMode="decimal" value={form.carbs} onChange={(event) => update("carbs", event.target.value)} placeholder="Optional" />
          <Input data-testid="meal-fat-input" label="Fat (g)" type="number" min="0" step="0.1" inputMode="decimal" value={form.fat} onChange={(event) => update("fat", event.target.value)} placeholder="Optional" />
          <Input data-testid="meal-fiber-input" label="Fiber (g)" type="number" min="0" step="0.1" inputMode="decimal" value={form.fiber} onChange={(event) => update("fiber", event.target.value)} placeholder="Optional" />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="meal-notes" className="text-sm font-medium text-text">Notes</label>
          <textarea id="meal-notes" data-testid="meal-notes-input" value={form.notes} onChange={(event) => update("notes", event.target.value)} placeholder="Add details..." rows={3} className={cn("w-full resize-none rounded-xl border border-border bg-surface px-4 py-3 text-base text-text placeholder:text-text-muted", "focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent")} />
        </div>

        {error !== null && <p className="rounded-xl bg-danger/10 px-3 py-2 text-sm font-medium text-danger" role="alert">{error}</p>}
        <div className="flex flex-col gap-3 sm:flex-row-reverse">
          <Button data-testid="submit-meal-btn" type="submit" loading={isSaving} className="flex-1">{isQuickAdd ? "Log food" : "Save meal"}</Button>
          <Button data-testid="cancel-meal-btn" type="button" variant="secondary" disabled={isSaving} onClick={onClose} className="flex-1">Cancel</Button>
        </div>
      </form>
    </div>
  );
}
